import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ActiveLocation } from './model/active-location';

/**
 * The active module's sections as a scrolling tab row — shown ONLY below 900px of shell width,
 * where the rail has left the flow.
 *
 * This is not a second navigation. It is the SAME sections the rail's level two shows, rendered in
 * the one band where the rail is not on screen — the same dual-render convention the rail and the
 * drawer already use, and the standard's own rule that "a visible control must work at every
 * width" read from the other direction: below 900px, reaching another view of the module you are
 * in would otherwise cost opening the drawer, finding the module, and tapping the section. Three
 * taps for what the desktop does in one, on the module the CEO uses from a phone.
 *
 * It is deliberately NOT shown above 900px. There the rail carries the sections a few pixels away,
 * and a tab row repeating them would be the guidelines' "nothing named twice on the same screen"
 * with the two copies visible at once. `shell.css` owns that decision, in the same block that takes
 * the rail out of the flow, so the two can only ever change together.
 *
 * It replaced `FinanceSectionLayout`'s own tab bar, which hardcoded Finance's five views a second
 * time. Every module with sections gets this for free now, and no module declares its sections
 * twice.
 *
 * `Link`, not `NavLink`, for the reason `ModuleRail` records: the active section comes from the
 * model, and `NavLink` would overwrite it with its own path match.
 */
export function SectionTabs({ active }: { active: ActiveLocation }) {
  const { t } = useTranslation('shell');
  const sections = active.module?.sections ?? [];

  if (sections.length === 0) {
    return null;
  }

  return (
    <nav className="section-tabs" aria-label={t('nav.sections')}>
      {sections.map((section) => (
        <Link
          key={section.id}
          to={section.path}
          className="section-tab"
          aria-current={active.section?.id === section.id ? 'page' : undefined}
        >
          {section.label}
        </Link>
      ))}
    </nav>
  );
}
